import {
  View,
  Text,
  ScrollView,
  RefreshControl,
  Pressable,
} from "react-native";
import { useState, useCallback, useMemo, useRef } from "react";
import { useIsFocused, useRouter } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import {
  incomeTypeRequiresPerson,
  isSocialSecurityIncomeType,
  type IncomeType,
} from "@perdiem/trpc/income-types";
import { trpc } from "../../lib/trpc";
import { buildBudgetDisplayCategories } from "../../lib/budget-display";
import {
  Card,
  SectionHeader,
  LoadingSpinner,
  ErrorState,
  ProgressBar,
  Button,
  FormErrorBanner,
  IncomeStreamForm,
  IncomeStreamList,
  SocialSecurityEstimator,
  BudgetCategoryGroups,
  PageContainer,
  PageHeader,
  WebHead,
  PerDiemCard,
  Money,
} from "../../components";
import { CashFlowModal } from "../../components/CashFlowModal";
import { mapBudgetToCashFlowData } from "../../components/cashflow/mappers";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import { useWebClickOutside } from "../../hooks/useWebClickOutside";
import { useColors } from "../../lib/colors";
import { useFireImpact } from "../../hooks/useFireImpact";
import { formatCurrency, formatPercent } from "../../lib/utils";
import { groupAndSortCategories } from "../../lib/category-config";
import { useScreenReadyTelemetry } from "../../hooks/useScreenReadyTelemetry";

const DISCRETIONARY_GROUPS = new Set([
  "FOOD_AND_DRINK",
  "GENERAL_MERCHANDISE",
  "ENTERTAINMENT",
  "PERSONAL_CARE",
  "TRAVEL",
]);

const ANNUAL_GROUPS = new Set(["TRAVEL"]);

export default function BudgetScreen() {
  const isDesktop = useIsDesktop();
  const isFocused = useIsFocused();
  const c = useColors();
  const [refreshing, setRefreshing] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [addingIncome, setAddingIncome] = useState(false);
  const [showCashflow, setShowCashflow] = useState(false);
  const utils = trpc.useUtils();

  const householdQ = trpc.households.get.useQuery(undefined, { enabled: isFocused });
  const profilesQ = trpc.profiles.list.useQuery(undefined, { enabled: isFocused });
  const entriesQ = trpc.budget.getEntries.useQuery(undefined, { enabled: isFocused });
  const averagesQ = trpc.budget.averages.useQuery(undefined, { enabled: isFocused });
  const currentMonthActualsQ = trpc.budget.currentMonthActuals.useQuery(undefined, { enabled: isFocused });
  const currentYearActualsQ = trpc.budget.currentYearActuals.useQuery(undefined, { enabled: isFocused });
  const perDiemQ = trpc.budget.perDiem.useQuery(undefined, { enabled: isFocused });
  const budgetsQ = trpc.budget.list.useQuery(undefined, { enabled: isFocused });
  const lifeEventCoverageQ = trpc.budget.lifeEventCoverage.useQuery(undefined, { enabled: isFocused });
  const budgetCategoriesQ = trpc.budget.listPrimaryCategories.useQuery(undefined, { enabled: isFocused });
  const incomeQ = trpc.income.list.useQuery(undefined, { enabled: isFocused });
  const firePlanQ = trpc.fire.getPlan.useQuery(undefined, { enabled: isFocused });
  const accountsQ = trpc.accounts.list.useQuery(undefined, { enabled: isFocused });
  const checkInStatusQ = trpc.budget.checkInStatus.useQuery(undefined, { enabled: isFocused });
  const criticalDataReady = !(entriesQ.isLoading || averagesQ.isLoading);
  useScreenReadyTelemetry({
    ready: criticalDataReady,
    screenName: 'budget',
  });
  const cashflowQ = trpc.budget.currentYearCashflow.useQuery(undefined, { enabled: showCashflow });
  const cashflowData = useMemo(
    () => (cashflowQ.data ? mapBudgetToCashFlowData(cashflowQ.data) : undefined),
    [cashflowQ.data],
  );
  const router = useRouter();

  const createIncomeMutation = trpc.income.create.useMutation();
  const updateEntryMutation = trpc.budget.updateEntry.useMutation();
  const createEntryMutation = trpc.budget.createEntry.useMutation();
  const createBudgetMutation = trpc.budget.create.useMutation();
  const bulkLinkToGoalMutation = trpc.transactions.bulkLinkToGoal.useMutation({
    onSuccess: () => {
      // Linked transactions leave the recurring actuals and join the event's
      // tracked spend — refresh both sides.
      void Promise.all([
        utils.budget.lifeEventCoverage.invalidate(),
        utils.budget.currentMonthActuals.invalidate(),
        utils.budget.currentYearActuals.invalidate(),
        utils.transactions.invalidate(),
      ]);
    },
  });

  // Collapsible category groups state
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);

  const handleToggleGroup = useCallback((groupId: string) => {
    setExpandedGroupId((current) => (current === groupId ? null : groupId));
  }, []);

  // Budget editing state
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(
    null,
  );
  const [budgetDraft, setBudgetDraft] = useState("");
  const [savingCategoryId, setSavingCategoryId] = useState<string | null>(null);
  const [budgetError, setBudgetError] = useState<string | null>(null);

  const budgetSectionRef = useRef<any>(null);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await Promise.allSettled([
      entriesQ.refetch(),
      averagesQ.refetch(),
      currentMonthActualsQ.refetch(),
      currentYearActualsQ.refetch(),
      perDiemQ.refetch(),
      budgetCategoriesQ.refetch(),
      incomeQ.refetch(),
      firePlanQ.refetch(),
      accountsQ.refetch(),
      checkInStatusQ.refetch(),
    ]);
    setRefreshing(false);
  }, [entriesQ, averagesQ, currentMonthActualsQ, currentYearActualsQ, perDiemQ, budgetCategoriesQ, incomeQ, firePlanQ, accountsQ, checkInStatusQ]);

  const fireImpact = useFireImpact({
    perDiem: perDiemQ.data,
    firePlan: firePlanQ.data,
    accounts: accountsQ.data,
  });

  const handleAddIncome = useCallback(
    async (input: {
      name: string;
      monthlyAmount: number;
      type: IncomeType;
      personId: string | null;
      growthRate: number;
      begin: string | null;
      end: string | null;
      deductions?: Record<string, number>;
      payFrequency?: string;
    }) => {
      await createIncomeMutation.mutateAsync({
        name: input.name,
        type: input.type,
        amount: input.monthlyAmount,
        growthRate: input.growthRate,
        ...(input.personId ? { personId: input.personId } : {}),
        ...(input.begin ? { begin: input.begin } : {}),
        ...(input.end ? { end: input.end } : { end: null }),
        ...(input.deductions ?? {}),
        ...(input.payFrequency ? { payFrequency: input.payFrequency as "weekly" | "biweekly" | "semimonthly" | "monthly" } : {}),
      });
      await Promise.all([
        incomeQ.refetch(),
        utils.fire.getRecalcStatus.invalidate(),
      ]);
    },
    [createIncomeMutation, incomeQ, utils.fire.getRecalcStatus],
  );

  // --- Budget editing ---
  const startEditingBudget = (displayId: string, currentAmount: number) => {
    const category = categories.find((c) => c.displayId === displayId);
    const isAnnual = category && ANNUAL_GROUPS.has(category.plaidPrimary);
    
    setEditingCategoryId(displayId);
    // If annual, currentAmount passed is already multiplied by 12 in the display categories
    setBudgetDraft(String(Math.round(currentAmount * 100) / 100));
    setBudgetError(null);
  };

  const cancelEditingBudget = () => {
    setEditingCategoryId(null);
    setBudgetDraft("");
    setBudgetError(null);
  };

  const ensureActiveBudgetId = async () => {
    let budgetId = entriesQ.data?.[0]?.budgetId;
    if (!budgetId) {
      const loadedBudgets =
        budgetsQ.data ?? (await budgetsQ.refetch()).data ?? [];
      const existingBudget =
        loadedBudgets.find((b) => b.isActive) ?? loadedBudgets[0];
      budgetId = existingBudget?.id;
    }
    if (!budgetId) {
      await createBudgetMutation.mutateAsync({
        name: "My Budget",
        isActive: true,
      });
      const refreshedBudgets = (await budgetsQ.refetch()).data ?? [];
      budgetId = (
        refreshedBudgets.find((b) => b.isActive) ?? refreshedBudgets[0]
      )?.id;
    }
    return budgetId;
  };

  const handleSaveBudget = async (displayId: string) => {
    setBudgetError(null);
    let parsedAmount = parseFloat(budgetDraft);
    if (Number.isNaN(parsedAmount) || parsedAmount < 0) {
      setBudgetError("Please enter a valid budget amount.");
      return;
    }

    const category = categories.find((c) => c.displayId === displayId);
    if (!category) {
      setBudgetError("Could not find the selected budget category.");
      return;
    }
    if (category.isEditable === false) {
      setBudgetError("This entry is derived from a liability and must be updated there.");
      return;
    }
    const budgetCategoryId = category.budgetCategoryId;
    const isAnnual = category && ANNUAL_GROUPS.has(category.plaidPrimary);
    if (isAnnual) {
      parsedAmount = parsedAmount / 12;
    }

    try {
      setSavingCategoryId(displayId);

      const budgetId = await ensureActiveBudgetId();
      if (!budgetId) {
        setBudgetError("Could not create a budget.");
        return;
      }

      const existingEntry = entriesQ.data?.find(
        (e) => e.budgetCategoryId === budgetCategoryId && !e.isDerived,
      );
      if (existingEntry) {
        await updateEntryMutation.mutateAsync({
          budgetId,
          budgetCategoryId,
          amount: parsedAmount,
        });
      } else {
        await createEntryMutation.mutateAsync({
          budgetId,
          budgetCategoryId,
          amount: parsedAmount,
        });
      }
      await Promise.all([
        entriesQ.refetch(),
        perDiemQ.refetch(),
        cashflowQ.refetch(),
        checkInStatusQ.refetch(),
        utils.fire.getRecalcStatus.invalidate(),
      ]);
      cancelEditingBudget();
    } catch {
      setBudgetError("Could not update budget. Please try again.");
    } finally {
      setSavingCategoryId(null);
    }
  };

  const handleIncomeMutated = async () => {
    await Promise.all([
      incomeQ.refetch(),
      utils.fire.getRecalcStatus.invalidate(),
    ]);
  };

  const entries = entriesQ.data ?? [];
  const averages = averagesQ.data ?? [];
  const currentMonthActuals = currentMonthActualsQ.data ?? [];
  const currentYearActuals = currentYearActualsQ.data ?? [];
  const incomeStreams = incomeQ.data ?? [];
  const profiles = profilesQ.data ?? [];
  const isDemo = householdQ.data?.isDemo ?? false;
  const hasActiveBudgetEditor = editingCategoryId !== null;
  const unassignedWageCount = incomeStreams.filter(
    (stream) => incomeTypeRequiresPerson(stream.type) && !stream.personId,
  ).length;

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  
  const startOfMonth = new Date(currentYear, currentMonth, 1).getTime();
  const endOfMonth = new Date(currentYear, currentMonth + 1, 1).getTime();
  const monthProgress = (now.getTime() - startOfMonth) / (endOfMonth - startOfMonth);

  const startOfYear = new Date(currentYear, 0, 1).getTime();
  const endOfYear = new Date(currentYear + 1, 0, 1).getTime();
  const yearProgress = (now.getTime() - startOfYear) / (endOfYear - startOfYear);

  const activeStreams = incomeStreams.filter((s) => {
    if (s.begin && parseInt(s.begin.substring(0, 4), 10) > currentYear)
      return false;
    if (s.end && parseInt(s.end.substring(0, 4), 10) <= currentYear)
      return false;
    return true;
  });

  const monthActualsByCategoryId = new Map(
    currentMonthActuals.map((category) => [
      category.budgetCategoryId,
      Number(category.actual ?? 0),
    ]),
  );

  const yearActualsByCategoryId = new Map(
    currentYearActuals.map((category) => [
      category.budgetCategoryId,
      Number(category.actual ?? 0),
    ]),
  );

  const lifeEventsByCategoryId = new Map<
    number,
    NonNullable<typeof lifeEventCoverageQ.data>
  >();
  for (const event of lifeEventCoverageQ.data ?? []) {
    if (event.budgetCategoryId == null) continue;
    const list = lifeEventsByCategoryId.get(event.budgetCategoryId) ?? [];
    list.push(event);
    lifeEventsByCategoryId.set(event.budgetCategoryId, list);
  }

  const seenActualCategoryIds = new Set<number>();
  const categories = buildBudgetDisplayCategories(
    averages,
    entries,
    budgetCategoriesQ.data ?? [],
  ).map(
    (category) => {
      const isAnnual = ANNUAL_GROUPS.has(category.plaidPrimary);
      const monthlyBudgeted = category.entryAmount ?? 0;
      const shouldAttachActuals = !seenActualCategoryIds.has(category.budgetCategoryId);
      const monthActual = shouldAttachActuals
        ? (monthActualsByCategoryId.get(category.budgetCategoryId) ?? 0)
        : 0;
      const yearActual = shouldAttachActuals
        ? (yearActualsByCategoryId.get(category.budgetCategoryId) ?? 0)
        : 0;
      if (shouldAttachActuals) {
        seenActualCategoryIds.add(category.budgetCategoryId);
      }
      
      const budgeted = isAnnual ? monthlyBudgeted * 12 : monthlyBudgeted;
      const actual = isAnnual ? yearActual : monthActual;

      const suggestedBudget = Number(
        category.entryAmount ?? category.monthlyAverage ?? 0,
      );
      const ratio = budgeted > 0 ? actual / budgeted : 0;
      const overBudget = budgeted > 0 && actual > budgeted;

      let statusColor = 'bg-green-400';
      if (overBudget) {
        statusColor = 'bg-red-400';
      } else if (isAnnual && ratio > yearProgress) {
        statusColor = 'bg-amber-400';
      } else if (!isAnnual && ratio > monthProgress) {
        statusColor = 'bg-amber-400';
      }

      return {
        ...category,
        budgeted,
        actual,
        suggestedBudget: isAnnual ? suggestedBudget * 12 : suggestedBudget,
        ratio,
        overBudget,
        isAnnual,
        statusColor,
        monthProgress: isAnnual ? yearProgress : monthProgress,
        isDerived: category.isDerived,
        isEditable: category.isEditable,
        derivedSourceLabel: category.derivedSourceLabel,
        // Attach to the first occurrence only, mirroring the actuals dedupe
        ...(shouldAttachActuals && lifeEventsByCategoryId.has(category.budgetCategoryId)
          ? { lifeEvents: lifeEventsByCategoryId.get(category.budgetCategoryId)! }
          : {}),
      };
    },
  );

  const categoryGroups = groupAndSortCategories(categories);

  const monthlyGroups = categoryGroups.filter(g => !ANNUAL_GROUPS.has(g.plaidPrimary));
  const annualGroups = categoryGroups.filter(g => ANNUAL_GROUPS.has(g.plaidPrimary));

  const filteredMonthlyGroups = useMemo(
    () =>
      showAllCategories
        ? monthlyGroups
        : monthlyGroups.filter((g) => DISCRETIONARY_GROUPS.has(g.plaidPrimary)),
    [monthlyGroups, showAllCategories],
  );

  const filteredMonthlyCategories = useMemo(
    () => filteredMonthlyGroups.flatMap((g) => g.categories),
    [filteredMonthlyGroups],
  );

  const totalMonthlyBudgeted = filteredMonthlyCategories.reduce((s, c) => s + c.budgeted, 0);
  const totalMonthlyActual = filteredMonthlyCategories.reduce((s, c) => s + c.actual, 0);
  const overallMonthlyRatio = totalMonthlyBudgeted > 0 ? totalMonthlyActual / totalMonthlyBudgeted : 0;

  // Savings rate still uses ALL categories for the month
  const allActualMonth = categories.reduce((s, c) => {
    const actual = c.isAnnual ? (monthActualsByCategoryId.get(c.budgetCategoryId) ?? 0) : c.actual;
    return s + actual;
  }, 0);

  const monthlyIncome = activeStreams.reduce(
    (s, stream) => s + Number(stream.annualTakeHome ?? stream.annualAmount ?? 0) / 12,
    0,
  );
  const hasDeductions = activeStreams.some((s) => (s.totalDeductions ?? 0) > 0);

  useWebClickOutside(
    [budgetSectionRef],
    hasActiveBudgetEditor,
    cancelEditingBudget,
  );

  if (entriesQ.isLoading || averagesQ.isLoading) {
    return <LoadingSpinner />;
  }

  const screenError =
    entriesQ.error ?? averagesQ.error ?? currentMonthActualsQ.error ?? currentYearActualsQ.error ?? perDiemQ.error;
  if (screenError) {
    return (
      <ErrorState
        error={screenError}
        screen="budget"
        metadata={{ failedQueries: "budget.getEntries,budget.averages,budget.currentMonthActuals,budget.perDiem" }}
        onRetry={onRefresh}
      />
    );
  }

  return (
    <>
      <WebHead>
        <title>Budget – Per Diem</title>
      </WebHead>
      <ScrollView
        className="flex-1 bg-canvas dark:bg-canvas-dark"
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor="#2B7A50"
          />
        }
      >
        <PageContainer className={isDesktop ? "" : "p-4"}>
          <View className="gap-4">
            <View>
              <PageHeader
                title="Budget"
                subtitle="Monthly spending and income"
                action={
                  <Pressable
                    onPress={() => router.push('/(app)/recurring')}
                    hitSlop={8}
                    className="flex-row items-center gap-1"
                  >
                    <Ionicons name="repeat" size={18} color={c.textSecondary} />
                    <Text className="text-xs text-gray-600 dark:text-gray-400">
                      Recurring
                    </Text>
                  </Pressable>
                }
              />
            </View>

            {checkInStatusQ.data?.isDue && !isDemo && (
              <View className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-900 dark:bg-amber-950">
                <View className={isDesktop ? "flex-row items-center justify-between gap-3" : "gap-3"}>
                  <View className="flex-1">
                    <Text className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                      It's time for your 90-day budget check-in
                    </Text>
                    <Text className="mt-1 text-xs text-amber-800 dark:text-amber-200">
                      Review recent spending and recurring charges against your current budget.
                    </Text>
                  </View>
                  <Button
                    size="sm"
                    onPress={() => router.push('/(app)/budget-check-in')}
                  >
                    {checkInStatusQ.data?.hasInProgressCheckIn ? 'Resume check-in' : 'Start check-in'}
                  </Button>
                </View>
              </View>
            )}

            <View className={isDesktop ? "flex-row gap-4" : "gap-4"}>
              {/* Per Diem Hero Card */}
              <PerDiemCard
                perDiem={perDiemQ.data}
                fireImpact={fireImpact}
                className={isDesktop ? "flex-1" : ""}
                showExplanationLink
              />

              {/* Monthly Summary */}
              <Card className={isDesktop ? "flex-1" : ""}>
                <Text className="text-sm text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide mb-3">
                  Monthly Summary{!showAllCategories ? " (Discretionary)" : ""}
                </Text>
                {monthlyIncome > 0 && (
                  <View className="flex-row justify-between mb-2">
                    <Text className="text-sm text-gray-600 dark:text-gray-400">
                      {hasDeductions ? "Take-Home Income" : "Income"}
                    </Text>
                    <Money weight={500} className="text-sm text-forest-600 dark:text-green-500">
                      {formatCurrency(monthlyIncome)}
                    </Money>
                  </View>
                )}
                <View className="flex-row justify-between mb-2">
                  <Text className="text-sm text-gray-600 dark:text-gray-400">
                    Budgeted Spend (Monthly)
                  </Text>
                  <Money weight={500} className="text-sm text-gray-800 dark:text-gray-200">
                    {formatCurrency(totalMonthlyBudgeted)}
                  </Money>
                </View>
                <View className="flex-row justify-between mb-3">
                  <Text className="text-sm text-gray-600 dark:text-gray-400">
                    Actual Spend This Month
                  </Text>
                  <Money
                    weight={500}
                    className={`text-sm ${
                      totalMonthlyActual > totalMonthlyBudgeted &&
                      totalMonthlyBudgeted > 0
                        ? "text-red-600 dark:text-red-400"
                        : "text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    {formatCurrency(totalMonthlyActual)}
                  </Money>
                </View>
                {totalMonthlyBudgeted > 0 && (
                  <View className="mt-2 flex-row items-center gap-2">
                    <View className="flex-1">
                      <ProgressBar
                        value={overallMonthlyRatio}
                        color={
                          overallMonthlyRatio > 1
                            ? "bg-red-500"
                            : overallMonthlyRatio > monthProgress
                            ? "bg-amber-500"
                            : "bg-green-500"
                        }
                        marker={monthProgress}
                        height={8}
                      />
                    </View>
                    <Text
                      className={`text-xs font-medium min-w-[45px] text-right ${
                        overallMonthlyRatio > 1
                          ? "text-red-600 dark:text-red-400"
                          : overallMonthlyRatio > monthProgress
                          ? "text-amber-600 dark:text-amber-400"
                          : "text-forest-600 dark:text-green-500"
                      }`}
                    >
                      {formatPercent(overallMonthlyRatio)}
                    </Text>
                  </View>
                )}

                {monthlyIncome > 0 && allActualMonth > 0 && (
                  <View className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                    <View className="flex-row justify-between">
                      <Text className="text-sm text-gray-600 dark:text-gray-400">
                        Savings Rate
                      </Text>
                      <Text
                        className={`text-sm font-semibold ${
                          monthlyIncome > allActualMonth
                            ? "text-forest-600 dark:text-green-500"
                            : "text-red-600 dark:text-red-400"
                        }`}
                      >
                        {formatPercent(
                          monthlyIncome > 0
                            ? (monthlyIncome - allActualMonth) / monthlyIncome
                            : 0,
                        )}
                      </Text>
                    </View>
                  </View>
                )}
              </Card>
            </View>

            {/* Annual Cash Flow Button */}
            {monthlyIncome > 0 && (
              <Pressable
                onPress={() => setShowCashflow(true)}
                className="flex-row items-center justify-center gap-1.5 py-2"
              >
                <Ionicons name="analytics-outline" size={16} color={c.primary} />
                <Text className="text-sm font-medium text-forest-600 dark:text-green-500">
                  View Annual Cash Flow
                </Text>
                <Ionicons name="chevron-forward" size={14} color={c.primary} />
              </Pressable>
            )}

            {/* Category Breakdown */}
            <FormErrorBanner error={budgetError} />

            {/* Discretionary / All filter */}
            <View className="flex-row justify-end">
              <View className="flex-row rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                <Pressable
                  onPress={() => setShowAllCategories(false)}
                  className={`px-3 py-1.5 ${
                    !showAllCategories
                      ? "bg-forest-600"
                      : "bg-white dark:bg-gray-800"
                  }`}
                >
                  <Text
                    className={`text-xs font-medium ${
                      !showAllCategories
                        ? "text-white"
                        : "text-gray-600 dark:text-gray-400"
                    }`}
                  >
                    Discretionary
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() => setShowAllCategories(true)}
                  className={`px-3 py-1.5 ${
                    showAllCategories
                      ? "bg-forest-600"
                      : "bg-white dark:bg-gray-800"
                  }`}
                >
                  <Text
                    className={`text-xs font-medium ${
                      showAllCategories
                        ? "text-white"
                        : "text-gray-600 dark:text-gray-400"
                    }`}
                  >
                    All
                  </Text>
                </Pressable>
              </View>
            </View>

            {filteredMonthlyGroups.length > 0 ? (
              <View ref={budgetSectionRef}>
                <BudgetCategoryGroups
                  groups={filteredMonthlyGroups}
                  expandedGroupId={expandedGroupId}
                  onToggleGroup={handleToggleGroup}
                  editingCategoryId={editingCategoryId}
                  getDraftValue={() => budgetDraft}
                  onDraftChange={(_categoryId, text) => {
                    setBudgetDraft(text);
                    setBudgetError(null);
                  }}
                  onSave={handleSaveBudget}
                  onStartEditing={startEditingBudget}
                  onCancelEditing={cancelEditingBudget}
                  savingCategoryId={savingCategoryId}
                  getRowErrorMessage={(categoryId) =>
                    editingCategoryId === categoryId ? budgetError : null
                  }
                  isReadOnly={isDemo}
                  onBulkLinkToEvent={(goalId) =>
                    bulkLinkToGoalMutation.mutate({ goalId })
                  }
                  bulkLinkingGoalId={
                    bulkLinkToGoalMutation.isPending
                      ? bulkLinkToGoalMutation.variables?.goalId ?? null
                      : null
                  }
                />
              </View>
            ) : (
              <Card className="items-center py-6">
                <Text className="text-sm text-gray-500 dark:text-gray-400 text-center">
                  No monthly spending data yet.
                </Text>
              </Card>
            )}

            {/* Annual Travel Budget */}
            {annualGroups.length > 0 && (
              <View className="mt-2">
                <Text className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2 ml-1">
                  Annual Travel Budget
                </Text>
                <BudgetCategoryGroups
                  groups={annualGroups}
                  expandedGroupId={expandedGroupId}
                  onToggleGroup={handleToggleGroup}
                  editingCategoryId={editingCategoryId}
                  getDraftValue={() => budgetDraft}
                  onDraftChange={(_categoryId, text) => {
                    setBudgetDraft(text);
                    setBudgetError(null);
                  }}
                  onSave={handleSaveBudget}
                  onStartEditing={startEditingBudget}
                  onCancelEditing={cancelEditingBudget}
                  savingCategoryId={savingCategoryId}
                  getRowErrorMessage={(categoryId) =>
                    editingCategoryId === categoryId ? budgetError : null
                  }
                  isReadOnly={isDemo}
                  onBulkLinkToEvent={(goalId) =>
                    bulkLinkToGoalMutation.mutate({ goalId })
                  }
                  bulkLinkingGoalId={
                    bulkLinkToGoalMutation.isPending
                      ? bulkLinkToGoalMutation.variables?.goalId ?? null
                      : null
                  }
                />
              </View>
            )}

            {/* Income Sources */}
            {!isDemo && addingIncome && (
              <IncomeStreamForm
                profiles={profiles}
                onAddIncome={async (input) => {
                  await handleAddIncome(input);
                  setAddingIncome(false);
                }}
                loading={createIncomeMutation.isPending}
                initiallyOpen
                onCancel={() => setAddingIncome(false)}
              />
            )}
            {incomeStreams.length > 0 ? (
              <Card>
                <SectionHeader
                  title="Income Sources"
                  action={
                    !isDemo ? (
                      <Pressable
                        onPress={() => setAddingIncome(true)}
                        className="p-1"
                      >
                        <Ionicons name="add" size={20} color={c.primary} />
                      </Pressable>
                    ) : undefined
                  }
                />
                {unassignedWageCount > 0 && (
                  <View className="mb-3 rounded-xl bg-amber-50 dark:bg-amber-900/30 px-4 py-3">
                    <Text className="text-sm text-amber-800 dark:text-amber-300">
                      {unassignedWageCount} wage income stream
                      {unassignedWageCount === 1 ? "" : "s"} still need a person
                      assignment. Update them so person-specific FIRE target
                      years stop the right salary.
                    </Text>
                  </View>
                )}
                <IncomeStreamList
                  streams={incomeStreams}
                  profiles={profiles}
                  onMutated={handleIncomeMutated}
                  readOnly={isDemo}
                />
              </Card>
            ) : (
              <Card className="items-center py-6">
                <Text className="text-sm text-gray-500 dark:text-gray-400 text-center">
                  No income streams yet.
                </Text>
                {!isDemo && (
                  <Pressable
                    onPress={() => setAddingIncome(true)}
                    className="mt-2 p-1"
                  >
                    <Ionicons name="add-circle-outline" size={24} color={c.primary} />
                  </Pressable>
                )}
              </Card>
            )}
            {!isDemo &&
              profilesQ.data &&
              !incomeStreams.some((s) =>
                isSocialSecurityIncomeType(s.type),
              ) && (
                <SocialSecurityEstimator
                  profiles={profilesQ.data ?? []}
                  incomeStreams={incomeStreams}
                />
              )}
          </View>
        </PageContainer>
      </ScrollView>
      <CashFlowModal
        visible={showCashflow}
        onClose={() => setShowCashflow(false)}
        data={cashflowData}
        isLoading={cashflowQ.isLoading}
        onTaxDashboardPress={() => {
          setShowCashflow(false);
          router.push("/tax-dashboard" as never);
        }}
      />
    </>
  );
}
